import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Loader2, Mail, MailCheck, RotateCw } from 'lucide-react';
import { Appointment } from '../../services/appointmentsService';

interface AppointmentInvitationControlProps {
  appointment: Appointment;
  /** שליחת ההזמנה בפועל (כולל toast ורענון). יש לזרוק שגיאה בכשל. */
  onSend: (appointmentId: number) => Promise<void>;
}

// ClientConfirmationStatus.Pending = 1 | AppointmentStatus.Completed = 3, Cancelled = 4
const CONFIRMATION_PENDING = 1;
const STATUS_COMPLETED = 3;
const STATUS_CANCELLED = 4;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #16a34a;
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--text-secondary);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--text-secondary);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ResendLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border: none;
  border-radius: 5px;
  background: none;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--primary-light);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const formatSentAt = (dateString?: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * מצב ההזמנה במייל של פגישה + שליחה / שליחה חוזרת.
 * בזמן השליחה הכפתור ננעל ומציג "שולח..." (השליחה ל-Azure נמשכת כמה שניות) — מונע שליחה כפולה,
 * שהייתה יוצרת טוקן חדש ומבטלת את הקישור הראשון שהלקוח קיבל.
 */
const AppointmentInvitationControl: React.FC<AppointmentInvitationControlProps> = ({ appointment, onSend }) => {
  const [sending, setSending] = useState(false);

  // שליחה (או שליחה חוזרת) מאפסת את האישור ל"ממתין" — רק לפגישה עם מועד שהלקוח עוד לא אישר/ביטל
  const canSend = !!appointment.appointmentDateTime
    && appointment.clientConfirmationStatus === CONFIRMATION_PENDING
    && appointment.status !== STATUS_COMPLETED
    && appointment.status !== STATUS_CANCELLED;

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      await onSend(appointment.id);
    } catch {
      // ההודעה מוצגת ע"י הקורא (toast)
    } finally {
      setSending(false);
    }
  };

  const handleResend = () => {
    if (!window.confirm('לשלוח שוב את ההזמנה? הקישור הקודם שנשלח יפסיק לעבוד.')) return;
    send();
  };

  if (!appointment.emailInvitationSent && !canSend) return null;

  return (
    <Wrap>
      {appointment.emailInvitationSent && (
        <SentBadge>
          <MailCheck size={14} strokeWidth={2} />
          הזמנה נשלחה{appointment.emailInvitationSentAt ? ` · ${formatSentAt(appointment.emailInvitationSentAt)}` : ''}
        </SentBadge>
      )}

      {canSend && appointment.emailInvitationSent && (
        <ResendLink type="button" onClick={handleResend} disabled={sending}>
          {sending ? <Spinner size={12} /> : <RotateCw size={12} strokeWidth={2} />}
          {sending ? 'שולח...' : 'שלח שוב'}
        </ResendLink>
      )}

      {canSend && !appointment.emailInvitationSent && (
        <SendButton type="button" onClick={send} disabled={sending}>
          {sending ? <Spinner size={14} /> : <Mail size={14} strokeWidth={2} />}
          {sending ? 'שולח...' : 'שלח הזמנה במייל'}
        </SendButton>
      )}
    </Wrap>
  );
};

export default AppointmentInvitationControl;
